import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from 'dompurify';
import ReCAPTCHA from "react-google-recaptcha";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  CheckCircleIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Notification from "../components/Notification";

interface WaiverField {
  id: string;
  type:
    | "name"
    | "email"
    | "phone"
    | "address"
    | "dateOfBirth"
    | "allergies"
    | "emergencyContactName"
    | "emergencyContactPhone"
    | "parentGuardianName"
    | "parentGuardianPhone"
    | "eSignature"
    | "customCheckbox"
    | "smsOptIn";
  label: string;
  required: boolean;
  included: boolean;
  order: number;
  studioName?: string; // For SMS Opt-In field
}

interface Waiver {
  id: string;
  title: string;
  content: string;
  fields: WaiverField[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  signed_count: number;
  studio_id: string;
}

interface StudentEntry {
  id: string;
  firstName: string;
  lastName: string;
  formData: Record<string, any>;
}

const FIELD_ICONS = {
  name: UserIcon,
  email: EnvelopeIcon,
  phone: PhoneIcon,
  address: MapPinIcon,
  dateOfBirth: CalendarIcon,
  allergies: ExclamationTriangleIcon,
  emergencyContactName: ExclamationTriangleIcon,
  emergencyContactPhone: PhoneIcon,
  parentGuardianName: UserIcon,
  parentGuardianPhone: PhoneIcon,
  eSignature: HandRaisedIcon,
  customCheckbox: CheckCircleIcon,
  smsOptIn: PhoneIcon,
};

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`;
};

// Date input formatter function
const formatDateInput = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numbers.slice(0, 8);
  
  // Format as MM/DD/YYYY
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  firstName: 50,
  lastName: 50,
  email: 254,
  phone: 20,
  address: 500,
  allergies: 500,
  emergencyContactName: 100,
  parentGuardianName: 100,
  dateOfBirth: 10,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For text fields (names, addresses, allergies), preserve spaces and only remove dangerous content
  if (fieldType === 'address' || 
      fieldType === 'allergies' ||
      fieldType === 'firstName' || 
      fieldType === 'lastName' || 
      fieldType === 'emergencyContactName' || 
      fieldType === 'parentGuardianName') {
    
    // Only remove dangerous patterns, preserve ALL spaces (including trailing)
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    
    // DON'T trim for text fields - preserve all spaces including trailing ones
    // Users should be able to type "John " and keep the space
    
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

  // Enhanced validation function
  const validateInput = (value: string, type: string): boolean => {
    if (!value || typeof value !== 'string') return false;
    
    const sanitized = sanitizeInput(value, type);
    if (sanitized.length === 0) return false;
  
  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'email':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized);
    case 'phone':
    case 'emergencyContactPhone':
    case 'parentGuardianPhone':
      const phoneRegex = /^[\d\s\-\(\)\.]+$/;
      return phoneRegex.test(sanitized) && sanitized.replace(/\D/g, '').length >= 10;
    case 'dateOfBirth':
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      return dateRegex.test(sanitized);
    case 'firstName':
    case 'lastName':
      // Allow letters, hyphens, apostrophes, periods for names (no spaces)
      // Check for dangerous patterns instead of restrictive allowed characters
      const nameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !nameDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'emergencyContactName':
    case 'parentGuardianName':
      // Allow letters, spaces, hyphens, apostrophes, periods for other names
      // Check for dangerous patterns instead of restrictive allowed characters
      const otherNameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !otherNameDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'address':
    case 'allergies':
      // Allow most characters for addresses and allergies, just exclude dangerous ones
      // Check for dangerous patterns instead of restrictive allowed characters
      const dangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !dangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};



export default function PublicWaiver() {
  const { waiverId } = useParams<{ waiverId: string }>();
  const navigate = useNavigate();
  const [waiver, setWaiver] = useState<Waiver | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [signatureData, setSignatureData] = useState<string>("");
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  // New state for multiple students
  const [studentEntries, setStudentEntries] = useState<StudentEntry[]>([
    {
      id: '1',
      firstName: '',
      lastName: '',
      formData: {}
    }
  ]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "general",
  });

  const closeNotification = () =>
    setNotification({ ...notification, show: false });

  useEffect(() => {
    if (waiverId) {
      fetchWaiver();
      fetchCsrfToken();
    }
  }, [waiverId]);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  const fetchWaiver = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/public/${waiverId}`
      );

      if (response.ok) {
        const data = await response.json();
        setWaiver(data);
      } else {
        setNotification({
          show: true,
          message: "Waiver not found or inactive",
          type: "error",
        });
        // Do NOT redirect, just set loading to false
        setWaiver(null);
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error loading waiver",
        type: "error",
      });
      setWaiver(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldType: string, value: any) => {
    let newValue = value;

    // Sanitize input first
    if (typeof value === 'string') {
      newValue = sanitizeInput(value, fieldType);
    }

    if (
      fieldType === "phone" ||
      fieldType === "emergencyContactPhone" ||
      fieldType === "parentGuardianPhone"
    ) {
      // Remove all non-digit characters
      const digits = newValue.replace(/\D/g, "");
      // Format as XXX-XXX-XXXX
      if (digits.length <= 3) {
        newValue = digits;
      } else if (digits.length <= 6) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else if (digits.length <= 10) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6
        )}`;
      } else {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
          10
        )}`;
      }
    } else if (fieldType === "dateOfBirth") {
      // Apply date formatting for dateOfBirth field
      newValue = formatDateInput(newValue);
    }

    // Validate input length
    if (MAX_LENGTHS[fieldType] && newValue.length > MAX_LENGTHS[fieldType]) {
      newValue = newValue.substring(0, MAX_LENGTHS[fieldType]);
    }

    // Update the current student's form data
    setStudentEntries(prev => prev.map((entry, index) => 
      index === currentStudentIndex 
        ? { ...entry, formData: { ...entry.formData, [fieldType]: newValue } }
        : entry
    ));
  };

  const handleNameChange = (field: 'firstName' | 'lastName', value: string) => {
    // Remove spaces from name input and sanitize
    const noSpacesValue = value.replace(/\s/g, '');
    const sanitizedValue = sanitizeInput(noSpacesValue, field);
    const validatedValue = sanitizedValue.substring(0, MAX_LENGTHS[field]);
    
    setStudentEntries(prev => prev.map((entry, index) => 
      index === currentStudentIndex 
        ? { ...entry, [field]: validatedValue }
        : entry
    ));
  };

  const addAnotherStudent = () => {
    // Get the current student's form data to inherit common information
    const currentStudent = studentEntries[currentStudentIndex];
    const inheritedFormData = { ...currentStudent.formData };
    
    // Clear the name fields, signature, and date of birth but keep all other information
    delete inheritedFormData.name;
    delete inheritedFormData.firstName;
    delete inheritedFormData.lastName;
    delete inheritedFormData.eSignature;
    delete inheritedFormData.dateOfBirth;
    
    const newStudent: StudentEntry = {
      id: Date.now().toString(),
      firstName: '',
      lastName: '',
      formData: inheritedFormData
    };
    setStudentEntries(prev => [...prev, newStudent]);
    setCurrentStudentIndex(prev => prev + 1);
    
    // Clear signature data for the new student
    setSignatureData("");
  };

  const removeStudent = (index: number) => {
    if (studentEntries.length > 1) {
      setStudentEntries(prev => prev.filter((_, i) => i !== index));
      if (currentStudentIndex >= index && currentStudentIndex > 0) {
        setCurrentStudentIndex(prev => prev - 1);
      }
      // Clear signature data when a student is removed
      setSignatureData("");
      // Clear the canvas as well
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
    }
  };

  const openSignatureModal = () => {
    setShowSignatureModal(true);
    // Set signature data to current student's signature if it exists
    const currentStudent = studentEntries[currentStudentIndex];
    const currentSignature = currentStudent?.formData?.eSignature || "";
    setSignatureData(currentSignature);
    
    // Draw existing signature on canvas if it exists
    if (currentSignature) {
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            const img = new Image();
            img.onload = () => {
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            };
            img.src = currentSignature;
          }
        }
      }, 100);
    }
  };

  const closeSignatureModal = () => {
    setShowSignatureModal(false);
    setSignatureData("");
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSignatureData("");
      }
    }
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const dataURL = canvas.toDataURL("image/png");
      setSignatureData(dataURL);
      
      // Save signature to current student's form data
      setStudentEntries(prev => prev.map((entry, index) => 
        index === currentStudentIndex 
          ? { ...entry, formData: { ...entry.formData, eSignature: dataURL } }
          : entry
      ));
      
      setShowSignatureModal(false);
      setNotification({
        show: true,
        message: "Signature saved",
        type: "success",
      });
    }
  };

  const getCanvasPos = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    
    // Calculate the scale factor between canvas size and display size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const pos = getCanvasPos(e);
    setLastPos(pos);
    
    // Start a new path immediately
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#000";
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    const newPos = getCanvasPos(e);
    if (lastPos) {
      ctx.lineTo(newPos.x, newPos.y);
      ctx.stroke();
    }
    setLastPos(newPos);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setLastPos(null);
  };

  // Add getTouchPos helper with better accuracy
  const getTouchPos = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0] || e.changedTouches[0];
    
    // Calculate the scale factor between canvas size and display size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY,
    };
  };

  const startTouchDrawing = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getTouchPos(e);
    setLastPos(pos);
    
    // Start a new path immediately
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#000";
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      }
    }
  };

  const drawTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    const newPos = getTouchPos(e);
    if (lastPos) {
      ctx.lineTo(newPos.x, newPos.y);
      ctx.stroke();
    }
    setLastPos(newPos);
  };

  const stopTouchDrawing = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(false);
    setLastPos(null);
  };

  const validateForm = () => {
    if (!waiver) return false;

    // Check if all students have required fields
    for (let i = 0; i < studentEntries.length; i++) {
      const student = studentEntries[i];
      
      // Check if student has a name
      if (!validateInput(student.firstName, 'firstName') || !validateInput(student.lastName, 'lastName')) {
        setNotification({
          show: true,
          message: `Student ${i + 1} must have both first and last name`,
          type: "error",
        });
        return false;
      }

      // For additional students (i > 0), they inherit data from the first student
      // But each student needs their own signature
      if (i === 0) {
        // Check required fields for the first student
        for (const field of waiver.fields) {
          // Skip name field validation since we handle firstName/lastName separately
          if (field.type === "name") continue;
          
          // Handle custom checkbox and SMS opt-in validation
          if (field.type === "customCheckbox" || field.type === "smsOptIn") {
            if (field.required && !student.formData[field.id] && student.formData[field.id] !== true) {
              setNotification({
                show: true,
                message: `SMS Opt-In is required for ${student.firstName} ${student.lastName}`,
                type: "error",
              });
              return false;
            }
            continue;
          }
          
          if (field.required && !student.formData[field.type]) {
            setNotification({
              show: true,
              message: `${field.label} is required for ${student.firstName} ${student.lastName}`,
              type: "error",
            });
            return false;
          }

          // Enhanced validation for each field type
          if (student.formData[field.type] && !validateInput(student.formData[field.type], field.type)) {
            setNotification({
              show: true,
              message: `Please enter a valid ${field.label.toLowerCase()} for ${student.firstName} ${student.lastName}`,
              type: "error",
            });
            return false;
          }
        }
      } else {
        // For additional students, check signature, date of birth, and custom checkboxes (name is handled separately)
        for (const field of waiver.fields) {
          // Handle custom checkbox and SMS opt-in validation
          if (field.type === "customCheckbox" || field.type === "smsOptIn") {
            if (field.required && !student.formData[field.id] && student.formData[field.id] !== true) {
              setNotification({
                show: true,
                message: `SMS Opt-In is required for ${student.firstName} ${student.lastName}`,
                type: "error",
              });
              return false;
            }
            continue;
          }
          
          if (field.required && (field.type === "eSignature" || field.type === "dateOfBirth") && !student.formData[field.type]) {
            setNotification({
              show: true,
              message: `${field.label} is required for ${student.firstName} ${student.lastName}`,
              type: "error",
            });
            return false;
          }
          
          // Validate date of birth format for additional students
          if (field.type === "dateOfBirth" && student.formData[field.type] && !validateInput(student.formData[field.type], field.type)) {
            setNotification({
              show: true,
              message: `Please enter a valid date of birth for ${student.firstName} ${student.lastName}`,
              type: "error",
            });
            return false;
          }
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        type: "error",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { waiverId });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          type: "error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // ReCaptcha validation (always required)
    if (!recaptchaValue && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      setNotification({
        show: true,
        message: "Please complete the reCAPTCHA verification",
        type: "error",
      });
      return;
    }

    setSubmitting(true);
    setLastSubmissionTime(now);

    try {
      // Prepare all student data for submission
      const allStudentsData = studentEntries.map((student, index) => {
        // For additional students, inherit missing data from the first student
        let finalFormData = { ...student.formData };
        if (index > 0 && waiver) {
          const firstStudent = studentEntries[0];
          // Inherit all fields except name-related ones, signatures, and date of birth
          for (const field of waiver.fields) {
            if (field.type !== "name" && field.type !== "eSignature" && field.type !== "dateOfBirth" && !finalFormData[field.type] && firstStudent.formData[field.type]) {
              finalFormData[field.type] = firstStudent.formData[field.type];
            }
          }
        }
        
        // Add the combined name to the form data for the backend
        finalFormData.name = `${student.firstName} ${student.lastName}`;

        return {
          formData: finalFormData,
          studentInfo: {
            ...finalFormData,
            name: `${student.firstName} ${student.lastName}`,
            firstName: student.firstName,
            lastName: student.lastName,
            submittedAt: new Date().toISOString(),
            userAgent: navigator.userAgent,
          }
        };
      });

      // Debug reCAPTCHA values
      console.log('Sending reCAPTCHA token:', recaptchaValue ? recaptchaValue.substring(0, 20) + '...' : 'null');
      console.log('reCAPTCHA site key:', process.env.REACT_APP_RECAPTCHA_SITE_KEY ? process.env.REACT_APP_RECAPTCHA_SITE_KEY.substring(0, 10) + '...' : 'not configured');

      // Submit all students in a single request
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/public/${waiverId}/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            students: allStudentsData,
            csrfToken,
            recaptchaToken: recaptchaValue,
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        
        // Try to get detailed error message
        try {
          const errorData = await response.json();
          console.error('Server error response:', errorData);
          throw new Error(errorData.error || 'Failed to submit waiver');
        } catch (parseError) {
          throw new Error('Failed to submit waiver');
        }
      }

      const result = await response.json();

      setShowThankYou(true);
      setStudentEntries([{
        id: '1',
        firstName: '',
        lastName: '',
        formData: {}
      }]);
      setCurrentStudentIndex(0);
      setSignatureData("");
      
      // Reset reCAPTCHA after successful submission
      setRecaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setNotification({
        show: true,
        message: `Thank you for filling out the waiver! ${studentEntries.length > 1 ? `${studentEntries.length} students have been` : 'Your details have been'} received and confirmed.${result.emails_sent ? ' A confirmation email has been sent.' : ''}`,
        type: "success",
      });
      
      // After 3 seconds, reset the thank you and reload the waiver page
      setTimeout(() => {
        setShowThankYou(false);
        fetchWaiver(); // Optionally re-fetch waiver data
      }, 3000);
    } catch (error) {
      logSecurityEvent('FORM_SUBMISSION_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        waiverId 
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Error submitting waiver",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: WaiverField) => {
    const IconComponent = FIELD_ICONS[field.type];
    const currentStudent = studentEntries[currentStudentIndex];
    const currentValue = (field.type === "customCheckbox" || field.type === "smsOptIn")
      ? (currentStudent?.formData[field.id] || false)
      : (currentStudent?.formData[field.type] || "");

    switch (field.type) {
      case "name":
        return (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Student Name{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  required
                  value={currentStudent?.firstName || ""}
                  onChange={(e) => handleNameChange('firstName', e.target.value)}
                  className={getInputClassName(
                    "block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  )}
                  placeholder="First Name"
                />
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  required
                  value={currentStudent?.lastName || ""}
                  onChange={(e) => handleNameChange('lastName', e.target.value)}
                  className={getInputClassName(
                    "block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  )}
                  placeholder="Last Name"
                />
              </div>
            </div>
          </div>
        );

      case "email":
      case "phone":
      case "emergencyContactName":
      case "emergencyContactPhone":
      case "parentGuardianName":
      case "parentGuardianPhone":
        return (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.label}{" "}
              {field.required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconComponent className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type={field.type === "email" ? "email" : "text"}
                required={field.required}
                value={currentValue}
                onChange={(e) => handleInputChange(field.type, e.target.value)}
                className={getInputClassName(
                  "block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                )}
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            </div>
          </div>
        );

      case "address":
      case "allergies":
        return (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.label}{" "}
              {field.required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                <IconComponent className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <textarea
                required={field.required}
                rows={3}
                value={currentValue}
                onChange={(e) => handleInputChange(field.type, e.target.value)}
                className={getInputClassName(
                  "block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                )}
                placeholder={field.type === "address" ? "Enter your address" : "List any allergies (food, medication, environmental, etc.)"}
              />
            </div>
          </div>
        );

      case "dateOfBirth":
        return (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.label}{" "}
              {field.required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconComponent className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type="text"
                required={field.required}
                value={currentValue}
                onChange={(e) => handleInputChange(field.type, e.target.value)}
                className={getInputClassName(
                  "block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                )}
                placeholder="MM/DD/YYYY"
              />
            </div>
          </div>
        );

      case "eSignature":
        return (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.label}{" "}
              {field.required && <span className="text-red-500">*</span>}
            </label>
            <div
              className="w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex items-center justify-center bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              onClick={openSignatureModal}
            >
              {currentValue ? (
                <img
                  src={currentValue}
                  alt="Signature"
                  className="max-h-20 max-w-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <HandRaisedIcon className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500 mb-1" />
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Click to sign
                  </span>
                </div>
              )}
            </div>
          </div>
        );

      case "customCheckbox":
        return (
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                required={field.required}
                checked={currentValue === true || currentValue === "true"}
                onChange={(e) => handleInputChange(field.id, e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                {field.label}{" "}
                {field.required && <span className="text-red-500">*</span>}
              </span>
            </label>
          </div>
        );

      case "smsOptIn":
        return (
          <div className="mb-6">
            <label className="flex items-start">
              <input
                type="checkbox"
                required={field.required}
                checked={currentValue === true || currentValue === "true"}
                onChange={(e) => handleInputChange(field.id, e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 mt-0.5 flex-shrink-0"
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                {field.label}{" "}
                {field.required && <span className="text-red-500">*</span>}
              </span>
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Loading Waiver
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we load the waiver form...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!waiver) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Waiver Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              The waiver you're looking for doesn't exist or is inactive.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Notification
        show={notification.show}
        message={notification.message}
        onClose={closeNotification}
        type={notification.type as any}
      />
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 p-6">
          <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-12 pb-12">
            <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
              {/* Header */}
              <div className="bg-indigo-600 px-6 py-4">
                <h1 className="text-2xl font-bold text-white">
                  {DOMPurify.sanitize(waiver.title, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                </h1>
                <p className="text-indigo-100 mt-1">
                  Please fill out this waiver completely
                </p>
              </div>

              {/* Form */}
              {showThankYou ? (
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" />
                  <h2 className="text-2xl font-bold mb-2 text-center text-gray-900 dark:text-white">
                    Thank you for filling out the waiver!
                  </h2>
                  <p className="text-lg text-gray-700 dark:text-gray-300 text-center">
                    {studentEntries.length > 1 
                      ? `${studentEntries.length} students have been` 
                      : 'Your details have been'} received and confirmed. Page will
                    redirect back to the beginning of the waiver.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Waiver Content */}
                  <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Waiver Terms
                    </h2>
                    <div className="prose prose-sm max-w-none text-gray-700 dark:text-gray-300">
                      <div 
                        className="whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(waiver.content, {
                            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li'],
                            ALLOWED_ATTR: []
                          })
                        }}
                      />
                    </div>
                  </div>

                  {/* Student Navigation */}
                  {studentEntries.length > 1 && (
                    <div className="mb-6 px-5">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Students ({studentEntries.length})
                        </h3>
                        <div className="flex space-x-2">
                          {studentEntries.map((student, index) => (
                            <button
                              key={student.id}
                              type="button"
                              onClick={() => setCurrentStudentIndex(index)}
                              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                index === currentStudentIndex
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                              }`}
                            >
                              {student.firstName && student.lastName 
                                ? `${student.firstName} ${student.lastName}`
                                : `Student ${index + 1}`
                              }
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Form Fields */}
                  {waiver.fields.length > 0 && (
                    <div className="mb-8 px-5">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {studentEntries.length > 1 
                            ? `Student ${currentStudentIndex + 1} Information`
                            : 'Student/Lead Information'
                          }
                        </h2>
                        {studentEntries.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeStudent(currentStudentIndex)}
                            className="inline-flex items-center px-3 py-1 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                            disabled={studentEntries.length === 1}
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            Remove Student
                          </button>
                        )}
                      </div>
                      
                      {/* Show inherited information notice for additional students */}
                      {studentEntries.length > 1 && currentStudentIndex > 0 && (
                        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            <strong>Note:</strong> This student will inherit the contact information from the first student. 
                            You only need to provide the student's name, date of birth, and signature below.
                          </p>
                        </div>
                      )}
                      
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        <i>Fields with a red asterisk are required.</i>
                      </p>
                      
                      {/* For additional students, show name fields, date of birth, signature, custom checkboxes, and SMS opt-in */}
                      {studentEntries.length > 1 && currentStudentIndex > 0 ? (
                        waiver.fields
                          .filter(field => field.type === "name" || field.type === "dateOfBirth" || field.type === "eSignature" || field.type === "customCheckbox" || field.type === "smsOptIn")
                          .map((field) => (
                            <div key={field.id}>{renderField(field)}</div>
                          ))
                      ) : (
                        /* For first student, show all fields */
                        waiver.fields.map((field) => (
                          <div key={field.id}>{renderField(field)}</div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Add Another Student Button */}
                  <div className="mb-6 px-5">
                    <button
                      type="button"
                      onClick={addAnotherStudent}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add Another Student
                    </button>
                  </div>

                  {/* ReCaptcha (always enabled) */}
                  {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                    <div className="mb-6 px-5">
                      <div className="flex justify-center">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                          onChange={setRecaptchaValue}
                          onExpired={() => setRecaptchaValue(null)}
                          theme="light"
                        />
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex justify-end pb-5 pr-5">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md shadow-sm text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-5 w-5 mr-2" />
                          Submit Waiver{studentEntries.length > 1 ? ` (${studentEntries.length} students)` : ''}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Signature Modal */}
          {showSignatureModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
                <div className="mt-3">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Electronic Signature
                      {studentEntries.length > 1 && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                          for {studentEntries[currentStudentIndex]?.firstName} {studentEntries[currentStudentIndex]?.lastName}
                        </span>
                      )}
                    </h3>
                    <button
                      onClick={closeSignatureModal}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Please sign your name in the box below using your mouse or
                      touch screen.
                    </p>

                    <div className="border-2 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                      <canvas
                        ref={canvasRef}
                        width={500}
                        height={200}
                        className="w-full h-48 cursor-crosshair"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startTouchDrawing}
                        onTouchMove={drawTouch}
                        onTouchEnd={stopTouchDrawing}
                        onTouchCancel={stopTouchDrawing}
                        style={{ touchAction: "none" }}
                      />
                      {signatureData && (
                        <img
                          src={signatureData}
                          alt="Current signature"
                          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                        />
                      )}
                    </div>

                    <div className="flex justify-between items-center">
                      <button
                        type="button"
                        onClick={clearSignature}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Clear
                      </button>

                      <div className="space-x-3">
                        <button
                          type="button"
                          onClick={closeSignatureModal}
                          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={saveSignature}
                          className="px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border border-indigo-600 dark:border-indigo-400 rounded-md hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Save Signature
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
